import {
  Badge,
  Button,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { KnowledgeBaseSpaceVisibilityForm } from '@/features/knowledge-bases/model';
import { InfoPopover } from '@/shared/ui';
import { VISIBILITY_ICONS } from '../../../lib';
import { CollectionsRailToggle } from '../CollectionsRailToggle';
import type { CollectionPaneHeaderProps } from './CollectionPaneHeader.types';

const VISIBILITY_TOOLTIP_KEYS = {
  organization: 'knowledgeBases.collectionPaneHeader.organizationVisibility',
  workspace: 'knowledgeBases.collectionPaneHeader.workspaceVisibility',
  internal: 'knowledgeBases.collectionPaneHeader.foundationVisibility',
} as const satisfies Record<KnowledgeBaseSpaceVisibilityForm, string>;

export function CollectionPaneHeader({
  name,
  description,
  visibility,
  visibilityLabel,
  workspaceCount,
  canEdit,
  onEdit,
  info,
  isLoading,
}: CollectionPaneHeaderProps) {
  const { t } = useTranslation('knowledge');
  const unknown = t('knowledgeBases.knowledgeBaseInfoPopover.unknown');
  const editLabel = t('knowledgeBases.collectionPaneHeader.editCollection');
  const VisibilityIcon = VISIBILITY_ICONS[visibility];

  return (
    <div data-testid="collection-pane-header" className="flex items-start justify-between gap-md">
      <div className="flex min-w-0 items-start gap-md">
        <CollectionsRailToggle />
        {isLoading ? (
          <div
            data-testid="collection-pane-header-skeleton"
            className="flex min-w-0 flex-1 flex-col gap-3xs"
          >
            {/* Heights track the resolved name and description line boxes, so nothing
                shifts when the collection arrives — the rail toggle beside them least
                of all, since the name row is sized to it. */}
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-5 w-48" />
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-2xs justify-center">
            <div className="flex min-w-0 items-center h-5">
              <div className="min-w-0 truncate leading-8 text-heading-lg font-medium tracking-tight">
                {name}
              </div>
            </div>
            {description && (
              <p className="truncate text-body-md  text-foreground-subtle">{description}</p>
            )}
          </div>
        )}
      </div>
      <div
        data-testid="collection-pane-header-actions"
        className="flex shrink-0 items-center gap-xs"
      >
        {visibilityLabel && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* A label, not a control: the arrow keeps it from reading as clickable,
                    and overrides the I-beam the text would otherwise draw. */}
                <Badge variant="secondary" roundness="full" className="cursor-default">
                  <VisibilityIcon aria-hidden="true" data-testid="collection-visibility-icon" />
                  {visibilityLabel}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {t(
                  visibility === 'workspace' && (workspaceCount ?? 0) > 1
                    ? 'knowledgeBases.collectionPaneHeader.sharedWorkspacesVisibility'
                    : VISIBILITY_TOOLTIP_KEYS[visibility],
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip>
            {/* The span keeps the trigger hoverable while the button is disabled. */}
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={editLabel}
                  // Nothing to edit until the collection is here, whatever the caller
                  // knows about permissions.
                  disabled={!canEdit || isLoading}
                  onClick={onEdit}
                >
                  <PencilIcon />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{editLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <InfoPopover
          label={t('knowledgeBases.knowledgeBaseInfoPopover.collectionInfo')}
          rows={[
            {
              label: t('knowledgeBases.knowledgeBaseInfoPopover.createdBy'),
              value: info.createdByUsername ?? unknown,
            },
            {
              label: t('knowledgeBases.knowledgeBaseInfoPopover.lastModified'),
              value: info.updated ?? unknown,
            },
            ...(info.sharedWith
              ? [
                  {
                    label: t('knowledgeBases.knowledgeBaseInfoPopover.sharedWith'),
                    value: info.sharedWith,
                  },
                ]
              : []),
          ]}
        />
      </div>
    </div>
  );
}

CollectionPaneHeader.displayName = 'CollectionPaneHeader';
